import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Alert,
  Row,
  Col,
  FormGroup,
} from 'reactstrap';
import { facturasService } from '../../services/facturasService';
import Loading from '../../components/Loading';

// Catálogos SAT para México
const USOS_CFDI = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I02', label: 'I02 - Mobilario y equipo de oficina por inversiones' },
  { value: 'I03', label: 'I03 - Equipo de transporte' },
  { value: 'I04', label: 'I04 - Equipo de computo y accesorios' },
  { value: 'I05', label: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
  { value: 'I06', label: 'I06 - Comunicaciones telefónicas' },
  { value: 'I07', label: 'I07 - Comunicaciones satelitales' },
  { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
  { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
  { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
  { value: 'D03', label: 'D03 - Gastos funerales' },
  { value: 'D04', label: 'D04 - Donativos' },
  { value: 'D05', label: 'D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)' },
  { value: 'D06', label: 'D06 - Aportaciones voluntarias al SAR' },
  { value: 'D07', label: 'D07 - Primas por seguros de gastos médicos' },
  { value: 'D08', label: 'D08 - Gastos de transportación escolar obligatoria' },
  { value: 'D09', label: 'D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones' },
  { value: 'D10', label: 'D10 - Pagos por servicios educativos (colegiaturas)' },
  { value: 'P01', label: 'P01 - Por definir' },
];

const FORMAS_PAGO = [
  { value: '01', label: '01 - Efectivo' },
  { value: '02', label: '02 - Cheque nominativo' },
  { value: '03', label: '03 - Transferencia electrónica de fondos' },
  { value: '04', label: '04 - Tarjeta de crédito' },
  { value: '05', label: '05 - Monedero electrónico' },
  { value: '06', label: '06 - Dinero electrónico' },
  { value: '08', label: '08 - Vales de despensa' },
  { value: '12', label: '12 - Dación en pago' },
  { value: '13', label: '13 - Pago por subrogación' },
  { value: '14', label: '14 - Pago por consignación' },
  { value: '15', label: '15 - Condonación' },
  { value: '17', label: '17 - Compensación' },
  { value: '23', label: '23 - Novación' },
  { value: '24', label: '24 - Confusión' },
  { value: '25', label: '25 - Remisión de deuda' },
  { value: '26', label: '26 - Prescripción o caducidad' },
  { value: '27', label: '27 - A satisfacción del acreedor' },
  { value: '28', label: '28 - Tarjeta de débito' },
  { value: '29', label: '29 - Tarjeta de servicios' },
  { value: '30', label: '30 - Aplicación de anticipos' },
  { value: '31', label: '31 - Intermediario pagos' },
  { value: '99', label: '99 - Por definir' },
];

const METODOS_PAGO = [
  { value: 'PUE', label: 'PUE - Pago en una sola exhibición' },
  { value: 'PPD', label: 'PPD - Pago en parcialidades o diferido' },
];

const TIPOS_COMPROBANTE = [
  { value: 'I', label: 'I - Ingreso' },
  { value: 'E', label: 'E - Egreso' },
  { value: 'T', label: 'T - Traslado' },
  { value: 'N', label: 'N - Nómina' },
  { value: 'P', label: 'P - Pago' },
];

function ConfigurarPrefacturaModal({ isOpen, toggle, venta, cliente, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prefactura, setPrefactura] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [huboCambios, setHuboCambios] = useState(false); // Para rastrear si se creó/actualizó la prefactura
  
  // Campos configurables
  const [serie, setSerie] = useState('A');
  const [usoCFDI, setUsoCFDI] = useState('G03');
  const [formaPago, setFormaPago] = useState('03');
  const [metodoPago, setMetodoPago] = useState('PUE');
  const [tipoComprobante, setTipoComprobante] = useState('I');
  const [condicionesPago, setCondicionesPago] = useState('');

  useEffect(() => {
    if (isOpen && venta) {
      // Cargar prefactura existente si existe
      cargarPrefactura();
    } else if (!isOpen) {
      // Resetear cuando se cierra el modal
      setPrefactura(null);
      setModoEdicion(false);
      setHuboCambios(false);
      setError('');
      setSuccess('');
      // Resetear formulario con valores por defecto
      setSerie('A');
      setUsoCFDI('G03');
      setFormaPago('03');
      setMetodoPago('PUE');
      setTipoComprobante('I');
      setCondicionesPago('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, venta]);

  const cargarPrefactura = async () => {
    if (!venta) return;
    
    try {
      const response = await facturasService.getByVenta(venta.VentaID || venta.ventaID);
      if (response.success && response.data) {
        const factura = response.data;
        const estatus = (factura.Estatus || factura.estatus || '').toLowerCase();
        
        // Si existe una prefactura (Borrador o Pendiente), cargarla
        if (estatus === 'borrador' || estatus === 'pendiente') {
          setPrefactura(factura);
          setModoEdicion(true);
          setSerie(factura.Serie || factura.serie || 'A');
          setUsoCFDI(factura.UsoCFDI || factura.usoCFDI || 'G03');
          setFormaPago(factura.FormaPago || factura.formaPago || '03');
          setMetodoPago(factura.MetodoPago || factura.metodoPago || 'PUE');
          setTipoComprobante(factura.TipoDeComprobante || factura.tipoDeComprobante || 'I');
          setCondicionesPago(factura.CondicionesDePago || factura.condicionesDePago || '');
        } else {
          // Si no existe o está timbrada, inicializar valores por defecto
          setPrefactura(null);
          setModoEdicion(false);
          resetearFormulario();
        }
      } else {
        // No existe prefactura, inicializar valores por defecto
        setPrefactura(null);
        setModoEdicion(false);
        resetearFormulario();
      }
    } catch (err) {
      // En caso de error, asumir que no existe y continuar
      setPrefactura(null);
      setModoEdicion(false);
      resetearFormulario();
    }
  };

  const resetearFormulario = () => {
    setSerie('A');
    // Usar el UsoCFDI del cliente si está disponible, sino usar G03 por defecto
    const usoCFDIDefault = cliente?.UsoCFDI || cliente?.usoCFDI || 'G03';
    setUsoCFDI(usoCFDIDefault);
    setFormaPago('03');
    setMetodoPago('PUE');
    setTipoComprobante('I');
    setCondicionesPago('');
  };

  const handleCrearOActualizar = async () => {
    setError('');
    setSuccess('');

    if (!venta) {
      setError('No se ha seleccionado una venta');
      return;
    }

    // Validaciones de campos obligatorios
    if (!serie || !serie.trim()) {
      setError('La Serie es requerida');
      return;
    }

    if (!tipoComprobante) {
      setError('El Tipo de Comprobante es requerido');
      return;
    }

    if (!usoCFDI) {
      setError('El Uso CFDI es requerido');
      return;
    }

    if (!formaPago) {
      setError('La Forma de Pago es requerida');
      return;
    }

    if (!metodoPago) {
      setError('El Método de Pago es requerido');
      return;
    }

    // Si el método de pago es PPD, las condiciones de pago son obligatorias
    if (metodoPago === 'PPD' && (!condicionesPago || !condicionesPago.trim())) {
      setError('Las Condiciones de Pago son requeridas cuando el método de pago es PPD (Pago en parcialidades)');
      return;
    }

    try {
      setLoading(true);

      const prefacturaData = {
        VentaID: venta.VentaID || venta.ventaID,
        Serie: serie.trim(),
        UsoCFDI: usoCFDI,
        FormaPago: formaPago,
        MetodoPago: metodoPago,
        TipoDeComprobante: tipoComprobante,
        // Solo enviar condiciones de pago si el método es PPD
        CondicionesDePago: metodoPago === 'PPD' ? condicionesPago.trim() : null,
      };

      let response;
      if (modoEdicion && prefactura) {
        // Actualizar prefactura existente
        const facturaId = prefactura.FacturaID || prefactura.facturaID;
        response = await facturasService.actualizarPrefactura(facturaId, prefacturaData);
      } else {
        // Crear nueva prefactura
        response = await facturasService.crearPrefactura(prefacturaData);
      }

      if (response.success) {
        setSuccess(modoEdicion ? 'Prefactura actualizada exitosamente' : 'Prefactura creada exitosamente');
        setPrefactura(response.data);
        setModoEdicion(true);
        setHuboCambios(true); // Marcar que hubo cambios
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.Message || response.message || 'Error al guardar la prefactura');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleTimbrar = async () => {
    if (!prefactura) {
      setError('Debe crear o actualizar la prefactura antes de timbrar');
      return;
    }

    setError('');
    setSuccess('');

    try {
      setLoading(true);
      const facturaId = prefactura.FacturaID || prefactura.facturaID;
      const response = await facturasService.timbrarPrefactura(facturaId);

      if (response.success) {
        setSuccess('Factura timbrada exitosamente');
        setHuboCambios(true); // Marcar que hubo cambios
        setTimeout(() => {
          onSuccess(); // Recargar lista cuando se timbra
        }, 1500);
      } else {
        setError(response.Message || response.message || 'Error al timbrar la factura');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Si hubo cambios (se creó/actualizó prefactura), recargar la lista
    if (huboCambios) {
      onSuccess();
    }
    
    setError('');
    setSuccess('');
    setPrefactura(null);
    setModoEdicion(false);
    setHuboCambios(false); // Resetear el flag
    resetearFormulario();
    toggle();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={handleClose} 
      size="lg" 
      className="modal-black"
      style={{ 
        marginTop: '-20vh',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}
      contentClassName="d-flex flex-column"
    >
      <ModalHeader toggle={handleClose}>
        {modoEdicion ? 'Configurar Prefactura' : 'Crear Prefactura'}
      </ModalHeader>
      <ModalBody style={{ maxHeight: 'calc(90vh - 150px)', overflowY: 'auto', flex: '1 1 auto' }}>
        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" className="mb-3">
            {success}
          </Alert>
        )}

        {venta && (
          <div className="mb-4">
            <h6>Información de la Venta</h6>
            <Row>
              <Col md="6">
                <p><strong>Folio:</strong> {venta.Folio || venta.folio || `#${venta.VentaID || venta.ventaID}`}</p>
                <p><strong>Cliente:</strong> {venta.ClienteNombre || venta.clienteNombre || cliente?.NombreRazonSocial || cliente?.nombreRazonSocial || 'Cliente General'}</p>
              </Col>
              <Col md="6">
                <p><strong>Subtotal:</strong> {formatCurrency(venta.Subtotal || venta.subtotal || 0)}</p>
                <p><strong>Impuestos:</strong> {formatCurrency(venta.Impuestos || venta.impuestos || 0)}</p>
                <p><strong>Total:</strong> {formatCurrency(venta.Total || venta.total || 0)}</p>
              </Col>
            </Row>
          </div>
        )}

        <hr />

        <h6 className="mb-3">Configuración de la Factura</h6>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label>Serie *</Label>
              <Input
                type="text"
                value={serie}
                onChange={(e) => setSerie(e.target.value)}
                placeholder="Ej: A"
                maxLength={10}
                required
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>Tipo de Comprobante *</Label>
              <Input
                type="select"
                value={tipoComprobante}
                onChange={(e) => setTipoComprobante(e.target.value)}
                required
              >
                {TIPOS_COMPROBANTE.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label>Uso CFDI *</Label>
              <Input
                type="select"
                value={usoCFDI}
                onChange={(e) => setUsoCFDI(e.target.value)}
                required
              >
                {USOS_CFDI.map((uso) => (
                  <option key={uso.value} value={uso.value}>
                    {uso.label}
                  </option>
                ))}
              </Input>
              <small className="text-muted">
                El cliente puede solicitar diferentes usos según el tipo de gasto
              </small>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>Forma de Pago *</Label>
              <Input
                type="select"
                value={formaPago}
                onChange={(e) => setFormaPago(e.target.value)}
                required
              >
                {FORMAS_PAGO.map((forma) => (
                  <option key={forma.value} value={forma.value}>
                    {forma.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label>Método de Pago *</Label>
              <Input
                type="select"
                value={metodoPago}
                onChange={(e) => {
                  setMetodoPago(e.target.value);
                  // Si cambia a PUE, limpiar condiciones de pago (no se permiten con PUE)
                  if (e.target.value === 'PUE') {
                    setCondicionesPago('');
                  }
                }}
                required
              >
                {METODOS_PAGO.map((metodo) => (
                  <option key={metodo.value} value={metodo.value}>
                    {metodo.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>
                Condiciones de Pago {metodoPago === 'PPD' ? '*' : '(Opcional - Solo para PPD)'}
              </Label>
              <Input
                type="text"
                value={condicionesPago}
                onChange={(e) => setCondicionesPago(e.target.value)}
                placeholder={metodoPago === 'PPD' ? "Ej: 3 meses sin interés, 30 días, etc." : "Solo disponible con método PPD"}
                maxLength={100}
                disabled={metodoPago === 'PUE'}
                required={metodoPago === 'PPD'}
              />
              {metodoPago === 'PUE' && (
                <small className="text-muted">
                  Las condiciones de pago solo se permiten cuando el método de pago es PPD (Pago en parcialidades)
                </small>
              )}
              {metodoPago === 'PPD' && (
                <small className="text-muted">
                  Requerido cuando el método de pago es PPD (Pago en parcialidades)
                </small>
              )}
            </FormGroup>
          </Col>
        </Row>

        {prefactura && (
          <div className="mt-3">
            <Alert color="info">
              <strong>Prefactura creada:</strong> Puede editar estos campos y guardar los cambios antes de timbrar.
            </Alert>
          </div>
        )}

        {/* Vista Previa de la Factura */}
        <hr className="my-4" />
        <h6 className="mb-3">Vista Previa de la Factura</h6>
        <div className="border rounded p-3 bg-light" style={{ backgroundColor: '#f8f9fa' }}>
          <Row>
            <Col md="6">
              <p className="mb-1"><strong>Serie:</strong> {serie || 'A'}</p>
              <p className="mb-1"><strong>Folio Venta:</strong> {venta?.Folio || venta?.folio || `#${venta?.VentaID || venta?.ventaID}`}</p>
              <p className="mb-1"><strong>Cliente:</strong> {venta?.ClienteNombre || venta?.clienteNombre || cliente?.NombreRazonSocial || cliente?.nombreRazonSocial || 'Cliente General'}</p>
              {cliente && (cliente.RFC || cliente.rfc) && (
                <p className="mb-1"><strong>RFC:</strong> {cliente.RFC || cliente.rfc}</p>
              )}
            </Col>
            <Col md="6">
              <p className="mb-1"><strong>Tipo de Comprobante:</strong> {
                TIPOS_COMPROBANTE.find(t => t.value === tipoComprobante)?.label || tipoComprobante
              }</p>
              <p className="mb-1"><strong>Uso CFDI:</strong> {
                USOS_CFDI.find(u => u.value === usoCFDI)?.label || usoCFDI
              }</p>
              <p className="mb-1"><strong>Forma de Pago:</strong> {
                FORMAS_PAGO.find(f => f.value === formaPago)?.label || formaPago
              }</p>
              <p className="mb-1"><strong>Método de Pago:</strong> {
                METODOS_PAGO.find(m => m.value === metodoPago)?.label || metodoPago
              }</p>
              {condicionesPago && (
                <p className="mb-1"><strong>Condiciones:</strong> {condicionesPago}</p>
              )}
            </Col>
          </Row>
          <hr />
          <Row>
            <Col md="12">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="mb-1"><strong>Subtotal:</strong> {formatCurrency(venta?.Subtotal || venta?.subtotal || 0)}</p>
                  <p className="mb-1"><strong>Impuestos:</strong> {formatCurrency(venta?.Impuestos || venta?.impuestos || 0)}</p>
                </div>
                <div className="text-right">
                  <h5><strong>Total: {formatCurrency(venta?.Total || venta?.total || 0)}</strong></h5>
                </div>
              </div>
            </Col>
          </Row>
          {prefactura && (
            <div className="mt-3">
              <Alert color="success" className="mb-0">
                <i className="tim-icons icon-check-2" /> Esta prefactura está lista para timbrar. Revise la información y haga clic en "Timbrar Factura" cuando esté listo.
              </Alert>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          color="primary" 
          onClick={handleCrearOActualizar} 
          disabled={loading || !venta}
          className="mr-2"
        >
          {loading ? 'Guardando...' : (modoEdicion ? 'Actualizar Prefactura' : 'Crear Prefactura')}
        </Button>
        {prefactura && (
          <Button 
            color="success" 
            onClick={handleTimbrar} 
            disabled={loading}
          >
            {loading ? 'Timbrando...' : 'Timbrar Factura'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default ConfigurarPrefacturaModal;

